import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';

import { BrixLogo } from '@/components/brand/BrixLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import {
  exchangeOAuthCode,
  signInWithMicrosoft,
  signInWithPassword,
  useSession,
} from '@/lib/auth';
import { SUPPORT_PHONE, SUPPORT_TEL_HREF } from '@/lib/constants';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password is too short'),
});

type LoginValues = z.infer<typeof loginSchema>;

interface LocationState {
  from?: { pathname?: string };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading } = useSession();
  const [serverError, setServerError] = useState<string | null>(null);
  // "Forgot password?" flow — the card flips into a reset-request form.
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // Microsoft sign-in returns here as /login?code=… (or ?error=…). Exchange
  // the code once (StrictMode mounts twice and the code is single-use), then
  // useSession picks up the new session and the redirect below fires.
  const [msBusy, setMsBusy] = useState(false);
  const exchanged = useRef(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const code = q.get('code');
    const err = q.get('error_description') || q.get('error');
    if (!code && !err) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (err) {
      setServerError(`Microsoft sign-in did not finish: ${err}`);
      return;
    }
    if (exchanged.current || !code) return;
    exchanged.current = true;
    setMsBusy(true);
    exchangeOAuthCode(code).then((r) => {
      setMsBusy(false);
      if (!r.ok) setServerError(`Microsoft sign-in did not finish: ${r.error}`);
    });
  }, []);

  async function onMicrosoft() {
    setServerError(null);
    setMsBusy(true);
    const r = await signInWithMicrosoft();
    if (r && !r.ok) {
      setMsBusy(false);
      setServerError(r.error);
    }
  }

  // Legacy welcome/recovery emails redirect here with tokens in the URL hash
  // (#access_token=…&type=recovery). Forward them to /set-password, which
  // knows how to consume the tokens and show the password-creation form.
  if (typeof window !== 'undefined' && window.location.hash.includes('type=recovery')) {
    return <Navigate to={`/set-password${window.location.hash}`} replace />;
  }

  if (!loading && session) {
    // '/' lets LandingRedirect route staff to the admin portal and
    // customers to /home.
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(values: LoginValues) {
    setServerError(null);
    const result = await signInWithPassword(values.email, values.password);
    if (!result.ok) {
      setServerError(result.error);
      return;
    }
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
    navigate(from, { replace: true });
  }

  function openForgot() {
    // Carry over whatever email they already typed on the login form.
    setResetEmail(getValues('email') ?? '');
    setResetSent(false);
    setResetError(null);
    setForgotMode(true);
  }

  async function onForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setResetError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail.trim())) {
      setResetError('Enter a valid email address.');
      return;
    }
    setResetSending(true);
    try {
      const res = await fetch('/.netlify/functions/forgot-password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setResetError(data.error ?? 'Something went wrong — please try again.');
        return;
      }
      setResetSent(true);
    } catch {
      setResetError('Something went wrong — please try again.');
    } finally {
      setResetSending(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-brix-surface flex items-center justify-center px-4 py-10">
      {/* Navy bloom backdrop — soft Brix-navy radial centered behind the card.
          Stays within brand: navy + amber only. Pure CSS, no asset. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 45% at 50% 30%, rgba(31,78,121,0.10), transparent 70%), radial-gradient(ellipse 35% 25% at 85% 90%, rgba(244,180,0,0.06), transparent 60%)',
        }}
      />
      {/* Faint grid texture for depth. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(31,78,121,1) 1px, transparent 1px), linear-gradient(90deg, rgba(31,78,121,1) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative w-full max-w-sm">
        {/* Wordmark above the card */}
        <div className="mb-8 flex justify-start">
          <BrixLogo variant="full" size="lg" showTagline showTrademark />
        </div>

        {/* Frosted glass card */}
        <div
          className="relative rounded-2xl overflow-hidden border border-brix-border bg-brix-surface/85 backdrop-blur-xl shadow-card-deep"
        >
          {/* Brix-navy accent strip on top of the card */}
          <div className="h-1.5 bg-brix-primary" aria-hidden />

          {/* Inner highlight on the top edge for a glass-rim effect */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-1.5 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
          />

          <div className="p-6">
            {forgotMode ? (
              <>
                <h1 className="font-display text-2xl font-semibold mb-1 text-brix-ink">
                  Reset your password
                </h1>
                <p className="text-sm text-brix-muted mb-6">
                  Enter your email and we&rsquo;ll send you a link to choose a new password.
                </p>

                {resetSent ? (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-brix-border bg-brix-primary-tint/60 px-3 py-3">
                      <p className="text-sm text-brix-ink leading-relaxed">
                        If an account exists for{' '}
                        <span className="font-semibold">{resetEmail.trim()}</span>, a reset
                        link is on its way. Check your inbox (and spam) — the link
                        expires after one use.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      className="w-full"
                      onClick={() => setForgotMode(false)}
                    >
                      Back to sign in
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={onForgotSubmit} className="space-y-4" noValidate>
                    <div className="space-y-1.5">
                      <Label htmlFor="reset-email">Email</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        autoComplete="email"
                        autoFocus
                        className="bg-brix-surface/80 backdrop-blur-sm"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                      />
                    </div>

                    {resetError && (
                      <div
                        role="alert"
                        className="rounded-lg border border-brix-border bg-brix-primary-tint/80 backdrop-blur-sm px-3 py-2"
                      >
                        <p className="text-xs text-brix-primary">{resetError}</p>
                      </div>
                    )}

                    <Button type="submit" size="lg" className="w-full" disabled={resetSending}>
                      {resetSending ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Sending…
                        </>
                      ) : (
                        'Send reset link'
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setForgotMode(false)}
                      className="block mx-auto text-xs text-brix-muted hover:text-brix-ink underline cursor-pointer"
                    >
                      Back to sign in
                    </button>
                  </form>
                )}
              </>
            ) : (
              <>
            <h1 className="font-display text-2xl font-semibold mb-1 text-brix-ink">Sign in</h1>
            <p className="text-sm text-brix-muted mb-6">
              For Brix Beverage wholesale customers.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  className="bg-brix-surface/80 backdrop-blur-sm"
                  {...register('email')}
                  aria-invalid={errors.email ? 'true' : 'false'}
                />
                {errors.email && (
                  <p className="text-xs text-brix-warning">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={openForgot}
                    className="text-[11px] text-brix-primary hover:text-brix-ink underline cursor-pointer"
                  >
                    Forgot password?
                  </button>
                </div>
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  className="bg-brix-surface/80 backdrop-blur-sm"
                  {...register('password')}
                  aria-invalid={errors.password ? 'true' : 'false'}
                />
                {errors.password && (
                  <p className="text-xs text-brix-warning">{errors.password.message}</p>
                )}
              </div>

              {serverError && (
                <div
                  role="alert"
                  className="rounded-lg border border-brix-border bg-brix-primary-tint/80 backdrop-blur-sm px-3 py-2"
                >
                  <p className="text-xs text-brix-primary">{serverError}</p>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full shadow-card hover:shadow-card-hover active:scale-[0.98] transition-all"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            {/* Staff only: the Azure provider is single-tenant, so only Brix
                company Microsoft accounts can complete it. */}
            <div className="my-4 flex items-center gap-3 text-[11px] text-brix-muted">
              <span className="h-px flex-1 bg-brix-border" />
              or
              <span className="h-px flex-1 bg-brix-border" />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={onMicrosoft}
              disabled={msBusy}
            >
              {msBusy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden>
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
              )}
              Brix staff: Sign in with Microsoft
            </Button>

            <p className="mt-6 text-[11px] text-brix-muted leading-relaxed">
              Trouble signing in? Call us at{' '}
              <a
                href={SUPPORT_TEL_HREF}
                className="underline text-brix-primary hover:text-brix-ink transition-colors duration-200"
              >
                {SUPPORT_PHONE}
              </a>
              .
            </p>
              </>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-[10px] text-brix-muted">
          Brix Beverage · Alameda, CA · 1951 Monarch St
        </p>
      </div>
    </div>
  );
}
